import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import { TrendingUp, MapPin, Package, CheckCircle, Clock, ArrowRight, Plus, Zap, Mail, Phone, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { StatCard, PageHeader, Card, SectionTitle, StatusBadge } from '../../components/common/UIComponents';
import { campaignsAPI } from '../../services/api';
import { Campaign } from '../../types';
import { dummyNotifications } from '../../data/dummyData';

export const BrandDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    campaignsAPI.getAll().then(c => {
      setCampaigns(c);
      setLoading(false);
    });
  }, []);

  const active = campaigns.filter(c => ['Approved', 'Field Staff Assigned', 'Installation Completed'].includes(c.status));
  const upcoming = campaigns.filter(c => ['Submitted', 'Admin Review', 'Payment Received'].includes(c.status));
  const completed = campaigns.filter(c => ['Campaign Closed', 'Proof Uploaded'].includes(c.status));

  return (
    <div style={{ background: 'var(--bg-base)', minHeight: '100vh', padding: '28px 32px' }} className="page-enter">

      {/* Welcome Banner */}
      <div style={{ background: 'linear-gradient(135deg, #1a0f07 0%, #3d1e0a 60%, #2d1508 100%)', borderRadius: 20, padding: '28px 32px', marginBottom: 28, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%', border: '1px solid rgba(232,98,26,0.2)' }} />
        <div style={{ position: 'absolute', bottom: -60, right: 80, width: 150, height: 150, borderRadius: '50%', border: '1px solid rgba(232,98,26,0.1)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'DM Sans', fontSize: 13, margin: '0 0 6px' }}>Good morning 👋</p>

              {/* Company name — primary heading */}
              <h2 style={{ color: '#fff', fontFamily: 'Playfair Display,serif', fontWeight: 700, fontSize: 26, margin: '0 0 4px', letterSpacing: '-0.02em' }}>
                {user?.companyName || user?.name}
              </h2>

              {/* Contact person + role badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', margin: '0 0 10px' }}>
                {user?.contactPerson && (
                  <span style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'DM Sans', fontSize: 13 }}>
                    {user.contactPerson}
                  </span>
                )}
                {user?.role && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'rgba(232,98,26,0.15)', border: '1px solid rgba(232,98,26,0.35)', color: '#e8621a', fontFamily: 'DM Sans', fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20, textTransform: 'capitalize' }}>
                    <ShieldCheck size={11} /> {user.role}
                  </span>
                )}
              </div>

              {/* Email + mobile */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', margin: '0 0 8px' }}>
                {user?.email && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.45)', fontFamily: 'DM Sans', fontSize: 12 }}>
                    <Mail size={12} /> {user.email}
                  </span>
                )}
                {user?.mobile && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.45)', fontFamily: 'DM Sans', fontSize: 12 }}>
                    <Phone size={12} /> {user.mobile}
                  </span>
                )}
              </div>

              <p style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'DM Sans', fontSize: 13, margin: 0 }}>
                You have <span style={{ color: '#e8621a', fontWeight: 600 }}>{active.length} active</span> and <span style={{ color: '#fbbf24', fontWeight: 600 }}>{upcoming.length} pending</span> campaigns
              </p>
            </div>
            <button onClick={() => navigate('/brand/search')} className="btn-brand" style={{ padding: '11px 22px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 }}>
              <Plus size={16} /> Book New Space
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {[
          { title: 'Active Campaigns', value: active.length, icon: <TrendingUp size={20} />, change: '2 more than last month', positive: true, color: '#22c55e' },
          { title: 'Upcoming Campaigns', value: upcoming.length, icon: <Clock size={20} />, color: '#3b82f6' },
          { title: 'Completed', value: completed.length, icon: <CheckCircle size={20} />, color: '#8b5cf6' },
          { title: 'Total Locations', value: campaigns.reduce((s, c) => s + c.locations, 0), icon: <MapPin size={20} />, color: 'var(--brand)' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} lg={3} key={i}><StatCard {...s} /></Grid>
        ))}
      </Grid>

      <Grid container spacing={2.5}>
        {/* Recent Campaigns */}
        <Grid item xs={12} lg={8}>
          <Card>
            <SectionTitle action={<button onClick={() => navigate('/brand/campaigns')} style={{ background: 'none', border: 'none', color: 'var(--brand)', cursor: 'pointer', fontFamily: 'DM Sans', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>View all <ArrowRight size={13} /></button>}>Recent Campaigns</SectionTitle>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)' }}>Loading...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {campaigns.map(c => (
                  <div key={c.id} onClick={() => navigate(`/brand/orders/${c.orderId}`)}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 16px', borderRadius: 12, border: '1px solid var(--border-light)', cursor: 'pointer', transition: 'all 0.18s', flexWrap: 'wrap', gap: 10 }}
                    onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-accent)'; (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--brand-mid)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border-light)'; }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 3 }}>{c.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {c.locations} location{c.locations > 1 ? 's' : ''} · ₹{c.budget.toLocaleString('en-IN')} · {new Date(c.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} – {new Date(c.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <StatusBadge status={c.status} size="sm" />
                      <ArrowRight size={14} color="var(--text-muted)" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </Grid>

        {/* Notifications */}
        <Grid item xs={12} lg={4}>
          <Card>
            <SectionTitle>Updates</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {dummyNotifications.map(n => (
                <div key={n.id} style={{ display: 'flex', gap: 12, padding: '11px 13px', borderRadius: 11, background: n.isRead ? 'transparent' : 'var(--bg-accent)', border: `1px solid ${n.isRead ? 'transparent' : 'var(--brand-mid)'}`, transition: 'all 0.18s' }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: n.type === 'success' ? 'var(--success)' : n.type === 'warning' ? '#f59e0b' : 'var(--info)', marginTop: 5, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: n.isRead ? 400 : 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>{n.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{n.message}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12}>
          <Card>
            <SectionTitle><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Zap size={15} color="var(--brand)" /> Quick Actions</span></SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
              {[
                { label: 'Search Ad Spaces', path: '/brand/search', icon: <Search />, color: 'var(--brand)' },
                { label: 'View Cart', path: '/brand/cart', icon: <Cart />, color: '#3b82f6' },
                { label: 'Track Orders', path: '/brand/orders', icon: <Package size={18} />, color: '#8b5cf6' },
                { label: 'View Invoices', path: '/brand/invoices', icon: <FileText />, color: '#22c55e' },
              ].map(a => (
                <button key={a.path} onClick={() => navigate(a.path)} style={{ background: 'var(--bg-muted)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.18s', fontFamily: 'DM Sans,sans-serif' }}
                  onMouseEnter={e => { (e.currentTarget).style.borderColor = a.color; (e.currentTarget).style.background = '#fff'; }}
                  onMouseLeave={e => { (e.currentTarget).style.borderColor = 'var(--border)'; (e.currentTarget).style.background = 'var(--bg-muted)'; }}>
                  <div style={{ color: a.color, marginBottom: 8 }}>{a.icon}</div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{a.label}</div>
                </button>
              ))}
            </div>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

const Search = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>;
const Cart = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="21" r="1" /><circle cx="19" cy="21" r="1" /><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" /></svg>;
const FileText = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><line x1="10" y1="9" x2="8" y2="9" /></svg>;